import request from 'sb-request'
import actions from 'redux/actions'
import { reducers } from 'redux/core'
import { getGqlQuery, modifyCosmeticsStructure } from 'common/helpers'

import productBaseFragment from 'helpers/fragments/productBase.graphql'
import cosmeticsDataQuery from './cosmeticsData.graphql'
import brandsQuery from './brandsQuery.graphql'


/**
 * @typedef {Object} Catalogue
 * @property {number} id
 * @property {boolean} fetchLeaves
 */

/**
 * Fetches cosmetics products via grapql (new!)
 *
 * @param {Object} opts
 * @param {Catalogue[]} opts.catalogues
 * @param {string[]} opts.brands
 * @property {boolean} isGrouped
 * @returns {Promise<any>}
 */
const fetchProductsNew = ({ catalogues, activeBrandFilters = [], isGrouped }) => {
  const hasBrands = Boolean(activeBrandFilters.length)

  const schema = catalogues.map(({ id, fetchLeaves }) => {
    const productsPart = `
       title: pageTitle
       ${getGqlQuery('products', { brands: activeBrandFilters })} {
          ...ProductBase
       }
    `

    const body = fetchLeaves ? (
      `${getGqlQuery('leaves', { extra: !hasBrands })} { ${productsPart} }`
    ) : (
      productsPart
    )

    return `
      ${getGqlQuery(`catalogue_${id}: catalogue`, { id })} {
        ${body}
      }
    `
  })

  const query = `{
    ${schema.join('')}
  }
  ${productBaseFragment}
  `

  actions.models.products.setFetching(true)

  return request.post('graphql?name=productsCatalogue', { body: { query } })
    .then((data) => {
      const catalogues = Object.values(data)

      if (isGrouped) {
        const groups = catalogues
          .reduce((result, { leaves, products, title }) => {
            if (leaves) {
              result = [ ...result, ...leaves ]
            }

            if (products) {
              result = [ ...result, { title, products } ]
            }

            return result
          }, [])
          .filter((group) => Boolean(group.products.length))

        const productCount = groups
          .reduce((result, group) => result + group.products.length, 0)

        return [ groups, productCount ]
      }

      const products = catalogues
        .map(({ leaves, products }) => {
          if (leaves) {
            return leaves
              .reduce((result, leaf) => [ ...result, ...leaf.products ], [])
          }

          if (products) {
            return products
          }

          return null
        })
        .filter((products) => Boolean(products))
        .reduce((result, products) => [ ...result, ...products ], [])

      return [ products, products.length ]
    })
    .then(([ products, productCount ]) => {
      reducers.models.products.setProducts(products)
      reducers.models.products.setProductCount(productCount)
    })
    .finally(() => actions.models.products.setFetching(false))
}

/**
 * Resets product to empty state
 *
 * @returns {void}
 */
const resetProducts = () => {
  reducers.models.products.setProducts([])
  reducers.models.products.setProductCount(0)
}

/**
 * Fetch Brands New
 * @param gqlQuery
 * @param activeBrandFilters {Array}
 * @returns {Promise<any | never>}
 */
const fetchBrandsNew = ({ input, activeBrandFilters }) => {
  reducers.models.products.setFetchingBrands(true)

  return request.post('graphql?name=brands', {
    body: {
      query: brandsQuery,
      variables: {
        input,
      },
    },
    modifyResult: ({ brands }) => brands,
  })
    .then(
      (brands) => {
        const brandsForFilters = brands.map((item) => ({ ...item, label: item.name, value: item.name }))
        const nextBrandsValue  = brandsForFilters.map((item) => item.value)
        const brandsToRemove   = activeBrandFilters.filter((brand) => nextBrandsValue.indexOf(brand) === -1)

        reducers.models.products.setFetchingBrands(false)

        brandsToRemove.forEach((value) => reducers.models.products.removeFilterValue({ filter: 'brands', value }))

        reducers.models.products.setBrands(brandsForFilters)

        return brands
      },
      () => {
        reducers.models.products.setFetchingBrands(false)
        reducers.models.products.setBrands([])
        return Promise.reject()
      }
    )
}

/**
 * Resets brands
 * TODO: Delete it after brands will be added to all of cosmetics
 *
 * @returns {void}
 */
const resetBrands = () => {
  reducers.models.products.setFetchingBrands(true)
  reducers.models.products.setBrands([])
}

const fetchCosmeticsDataStructure = () =>
  request.post('graphql?name=cosmeticsData', {
    body: {
      query: cosmeticsDataQuery,
      variables: { treeName: 'WellnessRoot' },
    },
  })
    .then(modifyCosmeticsStructure)
    .then((data) => {
      reducers.view.cosmeticsPage.setStructure(data)

      return data
    })


export default {
  fetchBrandsNew,
  resetBrands,
  fetchProductsNew,
  resetProducts,
  fetchCosmeticsDataStructure,
}
